import { Mic, MicOff, Phone, PhoneOff, Volume2 } from "lucide-react"
import { Link } from "react-router"

import { UserAvatar } from "@/components/chat/user-avatar"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { roomDisplayName } from "@/chat/format"
import type { VoicePeer } from "@/chat/protocol"
import { useChat } from "@/chat/store"
import { SELF } from "@/chat/voice"
import type { VoiceControls } from "@/components/chat/use-voice-controls"
import { cn } from "@/lib/utils"

/**
 * The room's voice chat, under the thread header. Out of the call it offers
 * to join one that is going on; in it, it shows who is there — the ring
 * lights up on whoever is talking — with the microphone and hang-up buttons.
 */
export function VoicePanel({
  roomId,
  controls,
}: {
  roomId: string
  controls: VoiceControls
}) {
  const { voice, call, setVoiceMuted } = useChat()
  const peers = voice[roomId] ?? []
  const { inThisRoom, toggle, error } = controls

  if (error) {
    return (
      <p
        role="alert"
        className="flex-none border-b px-4 py-2 text-xs text-destructive"
      >
        {error}
      </p>
    )
  }

  if (!inThisRoom && peers.length === 0) return null

  if (!inThisRoom) {
    return (
      <div className="flex flex-none items-center gap-3 border-b px-4 py-2">
        <Volume2 className="size-4 flex-none text-primary" />
        <div className="min-w-0 flex-1 truncate text-sm">
          <span className="font-medium">Голосовой чат</span>
          <span className="text-muted-foreground">
            {" · "}
            {peers.map((p) => p.user.name).join(", ")}
          </span>
        </div>
        <Button size="sm" onClick={() => void toggle()}>
          <Phone data-icon="inline-start" />
          Присоединиться
        </Button>
      </div>
    )
  }

  const joining = !call?.peerId

  return (
    <div className="flex flex-none flex-col gap-2 border-b px-4 py-3">
      {/* Padded inside the scroller, which would otherwise clip the ring. */}
      <div className="-mx-1 no-scrollbar flex gap-3 overflow-x-auto p-1">
        {joining && peers.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner className="size-4" />
            Подключение…
          </div>
        ) : (
          peers.map((peer) => <PeerTile key={peer.peerId} peer={peer} />)
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant={call?.muted ? "secondary" : "outline"}
          size="sm"
          aria-pressed={call?.muted}
          onClick={() => setVoiceMuted(!call?.muted)}
        >
          {call?.muted ? (
            <MicOff data-icon="inline-start" />
          ) : (
            <Mic data-icon="inline-start" />
          )}
          {call?.muted ? "Микрофон выключен" : "Микрофон"}
        </Button>
        <Button
          variant="destructive"
          size="sm"
          className="ml-auto"
          onClick={() => void toggle()}
        >
          <PhoneOff data-icon="inline-start" />
          Выйти
        </Button>
      </div>
    </div>
  )
}

function PeerTile({ peer }: { peer: VoicePeer }) {
  const { call } = useChat()
  const self = peer.peerId === call?.peerId
  const key = self ? SELF : peer.peerId
  const speaking = !peer.muted && !!call?.speaking.includes(key)
  const state = self ? "connected" : call?.connections[peer.peerId]
  const connecting = state !== "connected"

  return (
    <div className="flex w-14 flex-none flex-col items-center gap-1">
      <div className="relative">
        <div
          className={cn(
            "rounded-lg ring-2 ring-transparent ring-offset-2 ring-offset-background transition-shadow",
            speaking && "ring-primary"
          )}
        >
          <UserAvatar
            name={peer.user.name}
            className={cn("size-11", connecting && "opacity-50")}
          />
        </div>
        {peer.muted && (
          <span className="absolute -right-1 -bottom-1 flex size-5 items-center justify-center rounded-full bg-background">
            <MicOff className="size-3 text-destructive" />
          </span>
        )}
        {!self && connecting && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner className="size-4" />
          </span>
        )}
      </div>
      <span className="w-full truncate text-center text-[11px] text-muted-foreground">
        {self ? "Вы" : peer.user.name}
      </span>
    </div>
  )
}

/**
 * The call you are in, while you look at something else: which room, and
 * the two buttons that matter. Tapping it goes back to the room.
 */
export function CallBar({ activeRoomId }: { activeRoomId: string | null }) {
  const { call, rooms, voice, user, setVoiceMuted, leaveVoice } = useChat()
  if (!call || call.roomId === activeRoomId) return null

  const room = rooms.find((r) => r.id === call.roomId)
  const title = room ? roomDisplayName(room, user?.id ?? null) : "Голосовой чат"
  const count = voice[call.roomId]?.length ?? 0

  return (
    <div className="flex flex-none items-center gap-2 border-b bg-primary px-3 py-1.5 text-primary-foreground">
      <Link
        to={`/r/${encodeURIComponent(call.roomId)}`}
        className="flex min-w-0 flex-1 items-center gap-2 text-sm"
      >
        <Volume2 className="size-4 flex-none" />
        <span className="truncate font-medium">{title}</span>
        {count > 0 && <span className="flex-none opacity-80">· {count}</span>}
      </Link>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={call.muted ? "Включить микрофон" : "Выключить микрофон"}
        aria-pressed={call.muted}
        onClick={() => setVoiceMuted(!call.muted)}
        className="text-primary-foreground hover:bg-transparent hover:text-primary-foreground"
      >
        {call.muted ? <MicOff /> : <Mic />}
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Выйти из голосового чата"
        onClick={leaveVoice}
        className="text-primary-foreground hover:bg-transparent hover:text-primary-foreground"
      >
        <PhoneOff />
      </Button>
    </div>
  )
}
